import { computed, nextTick, ref, shallowRef, type Ref, watch } from 'vue'

type ReorderableItem = {
  id: string
}

export function useVisibleChipReorder<T extends ReorderableItem>(options: {
  visibleItems: Ref<T[]>
  canDrag: Ref<boolean>
  onReorder: (ids: string[]) => void
  onClickItem: (item: T) => void
}) {
  const visibleItemModel = shallowRef<T[]>([])
  const isDragging = ref(false)
  const suppressNextClick = ref(false)
  const suppressHover = ref(false)

  const canDragVisibleItems = computed(
    () => visibleItemModel.value.length > 1 && options.canDrag.value,
  )

  const handleDragStart = () => {
    isDragging.value = true
    suppressNextClick.value = true
    suppressHover.value = true
  }

  const handleDragChange = (event?: {
    moved?: {
      oldIndex: number
      newIndex: number
    }
  }) => {
    if (event?.moved) {
      const reorderedItems = [...options.visibleItems.value]
      const [movedItem] = reorderedItems.splice(event.moved.oldIndex, 1)

      if (movedItem) {
        reorderedItems.splice(event.moved.newIndex, 0, movedItem)
        visibleItemModel.value = reorderedItems
        options.onReorder(reorderedItems.map((item) => item.id))
        return
      }
    }

    options.onReorder(visibleItemModel.value.map((item) => item.id))
  }

  const handleDragEnd = () => {
    nextTick(() => {
      isDragging.value = false
      suppressNextClick.value = false
    })
  }

  const handlePointerMove = () => {
    if (suppressHover.value && !isDragging.value) {
      suppressHover.value = false
    }
  }

  const handlePointerLeave = () => {
    if (!isDragging.value) {
      suppressHover.value = false
    }
  }

  const handleItemClick = (item: T) => {
    if (suppressNextClick.value) {
      suppressNextClick.value = false
      return
    }

    options.onClickItem(item)
  }

  watch(
    options.visibleItems,
    (items) => {
      if (!isDragging.value) {
        visibleItemModel.value = [...items]
      }
    },
    { immediate: true },
  )

  return {
    visibleItemModel,
    isDragging,
    suppressHover,
    canDragVisibleItems,
    handleDragStart,
    handleDragChange,
    handleDragEnd,
    handlePointerMove,
    handlePointerLeave,
    handleItemClick,
  }
}
